import { getDataSource } from '@/lib/data-source';
import { deserializeFromJsonApi } from '@/lib/jsonapi/serializer';
import { CreditPackage, MultiCurrencyPrice, MultiLanguageString, TranslatedCreditPackage, TranslationParams } from '@/types/billing';

// Request/Response Types matching JSON:API format
export interface CreditPackageAttributes {
  name: string;
  nameTranslations?: MultiLanguageString;
  description?: string;
  descriptionTranslations?: MultiLanguageString;
  credits: number;
  prices: MultiCurrencyPrice;
  pricePerCredit?: { [currency: string]: number };
  defaultCurrency?: string;
  status: 'draft' | 'active' | 'archived';
  createdAt?: string;
  updatedAt?: string;
}

export interface CreditPackageCreateData {
  name: string;
  description?: string;
  credits: number;
  prices: MultiCurrencyPrice;
  priceIds?: { [currency: string]: string }; // For including existing price IDs in updates
  // New selective pricing structure
  pricing?: {
    prices: MultiCurrencyPrice;
    priceIds: { [currency: string]: string };
  };
  defaultCurrency?: string;
  status?: 'draft' | 'active' | 'archived';
  plan_type?: 'individual' | 'teams';
  features?: string[];
  featureFlags?: { [key: string]: boolean };
  isRecommended?: boolean;
  offerFreeTrial?: boolean;
  freeTrialDays?: number;
  trialRequiresCard?: boolean;
  translations?: {
    name?: MultiLanguageString;
    description?: MultiLanguageString;
    features?: Array<{ [locale: string]: string }>;
  };
}

export interface CreditPackageUpdateData extends Partial<CreditPackageCreateData> {}

export interface CreditPackageFilters {
  status?: 'active' | 'draft' | 'archived';
  minCredits?: number;
  maxCredits?: number;
  planType?: 'individual' | 'teams';
}

export interface CreditPackageSortOptions {
  field?: 'credits' | '-credits' | 'name' | '-name' | '-created-at';
}

export interface CreditPackageListResponse {
  data: CreditPackage[];
  meta?: {
    pagination?: {
      total: number;
      count: number;
      perPage: number;
      currentPage: number;
      totalPages: number;
    };
  };
  jsonapi: {
    version: string;
  };
}

class CreditPackagesService {
  private dataSource = getDataSource();

  /**
   * Format translation parameters for URL query string
   */
  private formatTranslationParams(params?: TranslationParams): URLSearchParams {
    const urlParams = new URLSearchParams();
    
    if (params?.lang) {
      urlParams.append('lang', params.lang);
    }
    
    if (params?.includeTranslations !== undefined) {
      if (typeof params.includeTranslations === 'boolean') {
        urlParams.append('include_translations', params.includeTranslations ? 'true' : 'false');
      } else if (typeof params.includeTranslations === 'string') {
        urlParams.append('include_translations', params.includeTranslations);
      } else if (Array.isArray(params.includeTranslations)) {
        urlParams.append('include_translations', params.includeTranslations.join(','));
      }
    }
    
    return urlParams;
  }

  /**
   * Calculate price per credit for each currency
   */
  private calculatePricePerCredit(prices: MultiCurrencyPrice, credits: number): { [currency: string]: number } {
    const pricePerCredit: { [currency: string]: number } = {};
    
    // Handle case where prices might be null or undefined
    if (!prices || typeof prices !== 'object') {
      return pricePerCredit;
    }
    
    for (const [currency, price] of Object.entries(prices)) {
      if (typeof price === 'number' && credits > 0) {
        pricePerCredit[currency] = Number((price / credits).toFixed(4));
      }
    }
    return pricePerCredit;
  }

  /**
   * Transform deserialized credit package to ensure prices are in the correct format
   */
  private transformCreditPackage(pkg: Record<string, unknown>): CreditPackage {
    const creditPackage = { ...pkg } as unknown as CreditPackage;
    
    console.log('Transforming credit package:', pkg);
    console.log('Initial creditPackage plan_type:', creditPackage.plan_type);
    
    // Map planType (camelCase from deserializer) to plan_type (snake_case for our interface)
    if ('planType' in pkg && typeof pkg.planType === 'string') {
      creditPackage.plan_type = pkg.planType as 'individual' | 'teams';
    }
    
    console.log('After plan_type mapping:', creditPackage.plan_type);
    
    // Check if prices already exists and is valid
    if (creditPackage.prices && typeof creditPackage.prices === 'object' && !Array.isArray(creditPackage.prices)) {
      console.log('Found valid prices object:', creditPackage.prices);
      return creditPackage;
    }
    
    // Convert pricesArray to prices object if needed
    if ('pricesArray' in pkg) {
      const pricesArray = pkg.pricesArray as Array<{
        currency: string;
        amount: number;
        pricePerCredit?: number;
        stripePriceId?: string;
      }>;
      
      console.log('Found pricesArray:', pricesArray);
      
      if (Array.isArray(pricesArray)) {
        creditPackage.prices = {};
        pricesArray.forEach(price => {
          if (typeof price.currency === 'string' && typeof price.amount === 'number') {
            creditPackage.prices[price.currency] = price.amount;
          }
        });
        console.log('Converted prices from pricesArray:', creditPackage.prices);
      }
    }
    
    // Ensure prices is at least an empty object
    if (!creditPackage.prices || typeof creditPackage.prices !== 'object') {
      console.log('No valid prices found, setting empty object');
      creditPackage.prices = {};
    }
    
    // Ensure plan_type is mapped even after price transformations
    if ('planType' in pkg && typeof pkg.planType === 'string' && !creditPackage.plan_type) {
      creditPackage.plan_type = pkg.planType as 'individual' | 'teams';
    }
    
    console.log('Final transformed package:', creditPackage);
    
    return creditPackage;
  }

  /**
   * Calculate savings compared to smallest package
   */
  calculateSavings(
    currentPricePerCredit: number,
    smallestPricePerCredit: number,
    credits: number,
    _currency: string
  ): { amount: number; percentage: number } {
    const savingsPerCredit = smallestPricePerCredit - currentPricePerCredit;
    const totalSavings = savingsPerCredit * credits;
    const percentageSavings = (savingsPerCredit / smallestPricePerCredit) * 100;
    
    return {
      amount: Number(totalSavings.toFixed(2)),
      percentage: Number(percentageSavings.toFixed(1))
    };
  }

  /**
   * Get all credit packages for a product
   */
  async getCreditPackages(
    productId: string,
    filters?: CreditPackageFilters,
    sort?: CreditPackageSortOptions,
    translationParams?: TranslationParams
  ): Promise<TranslatedCreditPackage[]> {
    try {
      const params = new URLSearchParams();
      
      // Add filters
      if (filters?.status) params.append('filter[status]', filters.status);
      if (filters?.minCredits !== undefined) params.append('filter[min_credits]', filters.minCredits.toString());
      if (filters?.maxCredits !== undefined) params.append('filter[max_credits]', filters.maxCredits.toString());
      if (filters?.planType) params.append('filter[plan_type]', filters.planType);
      
      // Add sorting
      if (sort?.field) params.append('sort', sort.field);
      
      // Add include parameter to get price IDs
      params.append('include', 'prices');
      
      // Add translation parameters
      const translationUrlParams = this.formatTranslationParams(translationParams);
      translationUrlParams.forEach((value, key) => {
        params.append(key, value);
      });
      
      const queryString = params.toString();
      const url = `/api/v1/products/${productId}/billing/credit-packages${queryString ? `?${queryString}` : ''}`;
      
      const response = await this.dataSource.get<CreditPackageListResponse>(url);
      
      // Deserialize JSON:API response
      const deserialized = deserializeFromJsonApi(response.data as unknown as Record<string, unknown>);
      
      // Return the data array
      if (deserialized && typeof deserialized === 'object' && 'data' in deserialized) {
        const rawPackages = (deserialized as { data: Record<string, unknown>[] }).data;
        
        // Transform and calculate price per credit for each package
        return rawPackages.map(rawPkg => {
          const pkg = this.transformCreditPackage(rawPkg);
          console.log('Transformed package:', pkg.name, 'plan_type:', pkg.plan_type, 'raw:', rawPkg);
          return {
            ...pkg,
            pricePerCredit: this.calculatePricePerCredit(pkg.prices, pkg.credits)
          };
        });
      }
      
      return [];
    } catch (error) {
      console.error(`Error fetching credit packages for product ${productId}:`, error);
      throw error;
    }
  }

  /**
   * Get a single credit package
   */
  async getCreditPackage(
    productId: string, 
    packageId: string,
    translationParams?: TranslationParams
  ): Promise<TranslatedCreditPackage> {
    try {
      const params = this.formatTranslationParams(translationParams);
      // Add include parameter to get price IDs
      params.append('include', 'prices');
      const queryString = params.toString();
      const url = `/api/v1/products/${productId}/billing/credit-packages/${packageId}${queryString ? `?${queryString}` : ''}`;
      
      const response = await this.dataSource.get<{ data: TranslatedCreditPackage }>(url);
      
      const deserialized = deserializeFromJsonApi(response.data as unknown as Record<string, unknown>);
      
      if (deserialized && typeof deserialized === 'object' && 'data' in deserialized) {
        const rawPackage = (deserialized as { data: Record<string, unknown> }).data;
        const creditPackage = this.transformCreditPackage(rawPackage);
        
        // Calculate price per credit
        return {
          ...creditPackage,
          pricePerCredit: this.calculatePricePerCredit(creditPackage.prices, creditPackage.credits)
        };
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      console.error(`Error fetching credit package ${packageId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new credit package
   */
  async createCreditPackage(
    productId: string,
    data: CreditPackageCreateData
  ): Promise<CreditPackage> {
    try {
      // Use selective pricing structure if available, otherwise fallback to legacy structure
      let pricesToUse: MultiCurrencyPrice;
      let priceIdsToUse: { [currency: string]: string } = {};
      
      if (data.pricing) {
        // New selective pricing structure
        pricesToUse = data.pricing.prices;
        priceIdsToUse = data.pricing.priceIds;
        console.log('CreditPackageService - Using new selective pricing structure');
        console.log('CreditPackageService - Selective prices:', pricesToUse);
        console.log('CreditPackageService - Selective price IDs:', priceIdsToUse);
      } else {
        // Legacy pricing structure
        pricesToUse = data.prices;
        priceIdsToUse = data.priceIds || {};
        console.log('CreditPackageService - Using legacy pricing structure');
      }

      // Convert MultiCurrencyPrice object to array format for the API
      const pricesArray = Object.entries(pricesToUse).map(([currency, amount]) => ({
        currency,
        amount,
        // Include price ID if available (for updates)
        ...(priceIdsToUse[currency] && { id: priceIdsToUse[currency] })
      }));
      console.log('CreditPackageService - createCreditPackage pricesArray:', pricesArray);

      // Prepare the request body in JSON:API format
      const requestBody = {
        data: {
          type: 'credit-packages',
          attributes: {
            name: data.name,
            description: data.description,
            credits: data.credits,
            prices: pricesArray,
            default_currency: data.defaultCurrency,
            status: data.status || 'draft',
            plan_type: data.plan_type || 'individual',
            ...(data.features && { features: data.features }),
            ...(data.featureFlags && { feature_flags: data.featureFlags }),
            ...(data.isRecommended !== undefined && { is_recommended: data.isRecommended }),
            // Handle trial fields - backend expects hyphenated format in requests
            ...(data.offerFreeTrial !== undefined && { 'offer-free-trial': data.offerFreeTrial }),
            ...(data.freeTrialDays !== undefined && { 'free-trial-days': data.freeTrialDays }),
            ...(data.trialRequiresCard !== undefined && { 'trial-requires-card': data.trialRequiresCard }),
            ...(data.translations && { translations: data.translations })
          }
        }
      };

      const response = await this.dataSource.post<{ data: CreditPackage }>(
        `/api/v1/products/${productId}/billing/credit-packages`,
        requestBody
      );
      
      console.log('Create credit package response:', response);
      
      // Handle both the deserialized format and raw API format
      let creditPackage: CreditPackage;
      
      // Check if response is already a CreditPackage (from deserialization)
      if (response.data && 'id' in response.data && 'name' in response.data) {
        creditPackage = response.data as unknown as CreditPackage;
      } else {
        // Try deserializing from JSON:API format
        const deserialized = deserializeFromJsonApi(response.data as unknown as Record<string, unknown>);
        
        if (deserialized && typeof deserialized === 'object' && 'data' in deserialized) {
          creditPackage = (deserialized as { data: CreditPackage }).data;
        } else {
          throw new Error('Invalid response format');
        }
      }
      
      // Ensure prices are in the correct format (convert from relationships if needed)
      if (!creditPackage.prices && response.data && typeof response.data === 'object') {
        const rawData = response.data as Record<string, unknown>;
        if (rawData.relationships && 
            typeof rawData.relationships === 'object' &&
            rawData.relationships !== null) {
          const relationships = rawData.relationships as Record<string, unknown>;
          if (relationships.prices && 
              typeof relationships.prices === 'object' &&
              relationships.prices !== null) {
            const pricesRel = relationships.prices as Record<string, unknown>;
            if (Array.isArray(pricesRel.data)) {
              creditPackage.prices = {};
              pricesRel.data.forEach((priceItem: unknown) => {
                if (typeof priceItem === 'object' && priceItem !== null) {
                  const price = priceItem as Record<string, unknown>;
                  if (typeof price.currency === 'string' && typeof price.amount === 'number') {
                    creditPackage.prices[price.currency] = price.amount;
                  }
                }
              });
            }
          }
        }
      }
      
      // Calculate price per credit
      return {
        ...creditPackage,
        pricePerCredit: this.calculatePricePerCredit(creditPackage.prices, creditPackage.credits)
      };
    } catch (error) {
      console.error(`Error creating credit package:`, error);
      throw error;
    }
  }

  /**
   * Update a credit package (full update)
   */
  async updateCreditPackage(
    productId: string,
    packageId: string,
    data: CreditPackageUpdateData
  ): Promise<CreditPackage> {
    try {
      // Use selective pricing structure if available, otherwise fallback to legacy structure
      let pricesToUse: MultiCurrencyPrice | undefined;
      let priceIdsToUse: { [currency: string]: string } = {};
      
      if (data.pricing) {
        // New selective pricing structure
        pricesToUse = data.pricing.prices;
        priceIdsToUse = data.pricing.priceIds;
        console.log('CreditPackageService - updateCreditPackage using new selective pricing structure');
        console.log('CreditPackageService - Selective prices:', pricesToUse);
        console.log('CreditPackageService - Selective price IDs:', priceIdsToUse);
      } else if (data.prices) {
        // Legacy pricing structure
        pricesToUse = data.prices;
        priceIdsToUse = data.priceIds || {};
        console.log('CreditPackageService - updateCreditPackage using legacy pricing structure');
      }

      // Convert MultiCurrencyPrice object to array format for the API if prices are provided
      const pricesArray = pricesToUse ? Object.entries(pricesToUse).map(([currency, amount]) => ({
        currency,
        amount,
        // Include price ID if available (for updates)
        ...(priceIdsToUse[currency] && { id: priceIdsToUse[currency] })
      })) : undefined;
      console.log('CreditPackageService - updateCreditPackage pricesArray:', pricesArray);

      const requestBody = {
        data: {
          type: 'credit-packages',
          id: packageId,
          attributes: {
            ...(data.name && { name: data.name }),
            ...(data.description !== undefined && { description: data.description }),
            ...(data.credits !== undefined && { credits: data.credits }),
            ...(pricesArray && { prices: pricesArray }),
            ...(data.defaultCurrency && { default_currency: data.defaultCurrency }),
            ...(data.status && { status: data.status }),
            ...(data.plan_type && { plan_type: data.plan_type }),
            ...(data.features !== undefined && { features: data.features }),
            ...(data.featureFlags !== undefined && { feature_flags: data.featureFlags }),
            ...(data.isRecommended !== undefined && { is_recommended: data.isRecommended }),
            // Handle trial fields - backend expects hyphenated format in requests
            ...(data.offerFreeTrial !== undefined && { 'offer-free-trial': data.offerFreeTrial }),
            ...(data.freeTrialDays !== undefined && { 'free-trial-days': data.freeTrialDays }),
            ...(data.trialRequiresCard !== undefined && { 'trial-requires-card': data.trialRequiresCard }),
            ...(data.translations && { translations: data.translations })
          }
        }
      };

      const response = await this.dataSource.put<{ data: CreditPackage }>(
        `/api/v1/products/${productId}/billing/credit-packages/${packageId}`,
        requestBody
      );
      
      console.log('Update credit package response:', response);
      
      // Handle both the deserialized format and raw API format
      let creditPackage: CreditPackage;
      
      // Check if response is already a CreditPackage (from deserialization)
      if (response.data && 'id' in response.data && 'name' in response.data) {
        creditPackage = response.data as unknown as CreditPackage;
      } else {
        // Try deserializing from JSON:API format
        const deserialized = deserializeFromJsonApi(response.data as unknown as Record<string, unknown>);
        
        if (deserialized && typeof deserialized === 'object' && 'data' in deserialized) {
          creditPackage = (deserialized as { data: CreditPackage }).data;
        } else {
          throw new Error('Invalid response format');
        }
      }
      
      // Ensure prices are in the correct format (convert from relationships if needed)
      if (!creditPackage.prices && response.data && typeof response.data === 'object') {
        const rawData = response.data as Record<string, unknown>;
        if (rawData.relationships && 
            typeof rawData.relationships === 'object' &&
            rawData.relationships !== null) {
          const relationships = rawData.relationships as Record<string, unknown>;
          if (relationships.prices && 
              typeof relationships.prices === 'object' &&
              relationships.prices !== null) {
            const pricesRel = relationships.prices as Record<string, unknown>;
            if (Array.isArray(pricesRel.data)) {
              creditPackage.prices = {};
              pricesRel.data.forEach((priceItem: unknown) => {
                if (typeof priceItem === 'object' && priceItem !== null) {
                  const price = priceItem as Record<string, unknown>;
                  if (typeof price.currency === 'string' && typeof price.amount === 'number') {
                    creditPackage.prices[price.currency] = price.amount;
                  }
                }
              });
            }
          }
        }
      }
      
      // Calculate price per credit
      return {
        ...creditPackage,
        pricePerCredit: this.calculatePricePerCredit(creditPackage.prices, creditPackage.credits)
      };
    } catch (error) {
      console.error(`Error updating credit package ${packageId}:`, error);
      throw error;
    }
  }

  /**
   * Partially update a credit package
   */
  async patchCreditPackage(
    productId: string,
    packageId: string,
    data: Partial<CreditPackageUpdateData>
  ): Promise<CreditPackage> {
    try {
      const requestBody = {
        data: {
          type: 'credit-packages',
          id: packageId,
          attributes: {
            ...(data.status && { status: data.status }),
            ...(data.name && { name: data.name }),
            ...(data.credits !== undefined && { credits: data.credits })
          }
        }
      };

      const response = await this.dataSource.patch<{ data: CreditPackage }>(
        `/api/v1/products/${productId}/billing/credit-packages/${packageId}`,
        requestBody
      );
      
      const deserialized = deserializeFromJsonApi(response.data as unknown as Record<string, unknown>);
      
      if (deserialized && typeof deserialized === 'object' && 'data' in deserialized) {
        const rawPackage = (deserialized as { data: Record<string, unknown> }).data;
        const creditPackage = this.transformCreditPackage(rawPackage);
        
        // Calculate price per credit
        return {
          ...creditPackage,
          pricePerCredit: this.calculatePricePerCredit(creditPackage.prices, creditPackage.credits)
        };
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      console.error(`Error patching credit package ${packageId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a credit package
   */
  async deleteCreditPackage(productId: string, packageId: string): Promise<void> {
    try {
      await this.dataSource.delete(
        `/api/v1/products/${productId}/billing/credit-packages/${packageId}`
      );
    } catch (error) {
      console.error(`Error deleting credit package ${packageId}:`, error);
      throw error;
    }
  }

  /**
   * Archive a credit package (safer than delete)
   */
  async archiveCreditPackage(productId: string, packageId: string): Promise<CreditPackage> {
    return this.patchCreditPackage(productId, packageId, { status: 'archived' });
  }

  /**
   * Find the best value package (lowest price per credit)
   */
  findBestValuePackage(packages: CreditPackage[], currency: string): CreditPackage | null {
    if (!packages || packages.length === 0) return null;
    
    return packages.reduce((best, current) => {
      const currentPricePerCredit = current.pricePerCredit?.[currency] || (current.prices[currency] / current.credits);
      const bestPricePerCredit = best.pricePerCredit?.[currency] || (best.prices[currency] / best.credits);
      
      return currentPricePerCredit < bestPricePerCredit ? current : best;
    });
  }

  /**
   * Find the smallest package (lowest credit amount)
   */
  findSmallestPackage(packages: CreditPackage[]): CreditPackage | null {
    if (!packages || packages.length === 0) return null;
    
    return packages.reduce((smallest, current) => {
      return current.credits < smallest.credits ? current : smallest;
    });
  }
}

export const creditPackagesService = new CreditPackagesService();